import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { Tooltip as Hint } from '@parrot/ui';
import { cx } from '@parrot/utils';
import { formatCount } from '@/lib/format';
import styles from './ProfileSection.module.css';

export interface ProfileSectionProps {
  title: string;
  /** What the figures cover: "UCAN · Q3 2023 – Q2 2026". */
  scope?: string;
  /** Explanation behind the ⓘ beside the title. */
  hint?: ReactNode;
  /** Section controls, right-aligned in the heading row. */
  controls?: ReactNode;
  /** A way out to the full view, after the title: "Full leaderboard". */
  link?: { label: string; to: string };
  /** Boxed body (bordered card). Off for bodies that draw their own cards. */
  boxed?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A profile section, after the title profile's Revenue Over Time: heading with
 * scope line and ⓘ, controls on the right, then the boxed body. Every platform
 * profile tab is a stack of these, so the tabs read as one product.
 */
export function ProfileSection({ title, scope, hint, controls, link, boxed = true, className, children }: ProfileSectionProps) {
  return (
    <section className={cx(styles.section, className)} data-print-keep>
      <header className={styles.header}>
        <div className={styles.heading}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{title}</h3>
            {hint && (
              <span data-print-hide>
                <Hint content={hint} placement="top">
                  <Info size={13} className={styles.hintIcon} />
                </Hint>
              </span>
            )}
            {link && (
              <>
                <span className={styles.titleDivider} aria-hidden="true">
                  |
                </span>
                <Link to={link.to} className={styles.titleLink} data-print-hide>
                  {link.label}
                </Link>
              </>
            )}
          </div>
          {scope && <p className={styles.scope}>{scope}</p>}
        </div>
        {controls && (
          <div className={styles.controls} data-print-hide>
            {controls}
          </div>
        )}
      </header>
      {boxed ? <div className={styles.box}>{children}</div> : children}
    </section>
  );
}

/** Ruled toolbar of labelled view controls, between the sentence and the plot. */
export function ChartToolbar({ children }: { children: ReactNode }) {
  return (
    <div className={styles.toolbar} data-print-hide>
      {children}
    </div>
  );
}

export function ToolbarGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.toolbarGroup}>
      <span className={styles.toolbarLabel}>{label}</span>
      {children}
    </div>
  );
}

/** Two-line quarter tick ("Q1" over "2020"), as the title profile's charts. */
export function QuarterTick(props: { x?: number; y?: number; payload?: { value: string } }) {
  const { x = 0, y = 0, payload } = props;
  const [q, year] = String(payload?.value ?? '').split(' ');
  return (
    <text x={x} y={y + 14} textAnchor="middle" className={styles.quarterTick}>
      <tspan x={x}>{q}</tspan>
      {year && (
        <tspan x={x} dy={16} className={styles.quarterTickYear}>
          {year}
        </tspan>
      )}
    </text>
  );
}

/** Axis tick count without trailing zeros: 40000000 → "40M", signed → "+40M". */
export function formatAxisCount(v: number, signed = false): string {
  const short = formatCount(Math.abs(v)).replace(/\.0+(?=[KMB])/, '').replace(/(\.\d*?)0+(?=[KMB])/, '$1');
  const sign = v < 0 ? '−' : signed && v > 0 ? '+' : '';
  return `${sign}${short}`;
}

/**
 * Inclusive [start, end] window over `count` quarters, defaulting to the last
 * `initial`. Held as distances from the newest quarter, so a platform with a
 * different history length keeps a sensible window.
 */
export function useQuarterWindow(count: number, initial = 12) {
  const [fromEnd, setFromEnd] = useState<[number, number] | null>(null);
  const max = Math.max(0, count - 1);
  const [a, b] = fromEnd ?? [Math.min(max, initial - 1), 0];
  const range: [number, number] = [Math.max(0, max - a), Math.max(0, max - b)];
  const setRange = ([lo, hi]: [number, number]) => setFromEnd([max - lo, max - hi]);
  return [range, setRange] as const;
}

export { styles as profileChartStyles };
